import { AmenityTypeCombobox } from '@/components/shared/AmenityTypeCombobox';
import { BusinessHoursEditor } from '@/components/shared/BusinessHoursEditor';
import { HotelCombobox } from '@/components/shared/HotelCombobox';
import { ImageUploadDropzone } from '@/components/shared/ImageUploadDropzone';
import {
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Label,
  Loading,
  PageContainer,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  useApiClient,
} from '@startupquickstart/frontend';
import { Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

interface AmenityType {
  id: string;
  name: string;
}

interface Hotel {
  id: string;
  name: string;
}

interface FormData {
  name: string;
  amenityTypeId: string;
  hotelId: string;
  status: 'published' | 'draft' | 'needs_review';
  featuredImageId?: string;
  description?: string;
  phoneNumber?: string;
  email?: string;
  website?: string;
  usesHotelAddress: boolean;
  floor?: string;
  capacity?: number;
  streetAddress?: string;
  streetAddress2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  hours?: Record<string, unknown>;
  amenityType?: AmenityType;
  hotel?: Hotel;
}

/**
 * Amenity create/edit form page with custom components
 */
export function AmenityFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    amenityTypeId: '',
    hotelId: '',
    status: 'published',
    usesHotelAddress: false,
  });

  /**
   * Load existing amenity data if editing
   */
  useEffect(() => {
    if (id) {
      setIsLoading(true);
      apiClient
        .get<{ data: FormData }>(`/amenities/${id}`)
        .then(response => {
          const data = response.data;
          setFormData({
            name: data.name || '',
            amenityTypeId: data.amenityTypeId || '',
            hotelId: data.hotelId || '',
            status: data.status || 'published',
            featuredImageId: data.featuredImageId,
            description: data.description,
            phoneNumber: data.phoneNumber,
            email: data.email,
            website: data.website,
            usesHotelAddress: data.usesHotelAddress || false,
            floor: data.floor,
            capacity: data.capacity,
            streetAddress: data.streetAddress,
            streetAddress2: data.streetAddress2,
            city: data.city,
            state: data.state,
            postalCode: data.postalCode,
            country: data.country,
            hours: data.hours,
            amenityType: data.amenityType,
            hotel: data.hotel,
          });
        })
        .catch(error => {
          toast.error(error?.message || 'Failed to load amenity');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [id, apiClient]);

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const endpoint = id ? `/amenities/${id}` : '/amenities';
      const method = id ? 'put' : 'post';

      const result = await apiClient[method]<{ data: { id: string } }>(endpoint, formData);

      toast.success(id ? 'Amenity updated successfully' : 'Amenity created successfully');
      navigate(`/amenities/${result.data.id || id}`);
    } catch (error) {
      toast.error((error as Error)?.message || 'Failed to save amenity');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <Loading className="m-auto" />
      </PageContainer>
    );
  }

  const breadcrumbs = id
    ? [
        { label: 'Home', href: '/' },
        { label: 'Amenities', href: '/amenities' },
        { label: 'Amenity', href: `/amenities/${id}` },
        { label: 'Edit', current: true },
      ]
    : [
        { label: 'Home', href: '/' },
        { label: 'Amenities', href: '/amenities' },
        { label: 'Create', current: true },
      ];

  return (
    <PageContainer>
      <form onSubmit={handleSubmit}>
        {/* Header */}
        <div className="mb-6">
          <Breadcrumbs items={breadcrumbs} className="mb-4" />
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold">{id ? 'Edit Amenity' : 'Create Amenity'}</h1>
            <Button type="submit" loading={isSaving} icon={Save}>
              {id ? 'Update Amenity' : 'Create Amenity'}
            </Button>
          </div>
        </div>

      {/* General Section */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>General</CardTitle>
          <CardDescription>General Information about the Amenity</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-6">
          <div className="grid gap-2">
            <Label htmlFor="name">
              Name<span className="text-destructive ml-1">*</span>
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter amenity name"
              required
            />
          </div>

          <AmenityTypeCombobox
            value={formData.amenityTypeId}
            onValueChange={value => setFormData({ ...formData, amenityTypeId: value })}
            required
            initialItem={formData.amenityType}
          />

          <HotelCombobox
            value={formData.hotelId}
            onValueChange={value => setFormData({ ...formData, hotelId: value })}
            required
            initialItem={formData.hotel}
          />

          <div className="grid gap-2">
            <Label htmlFor="status">Status</Label>
            <Select
              value={formData.status}
              onValueChange={(value: 'published' | 'draft' | 'needs_review') =>
                setFormData({ ...formData, status: value })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="needs_review">Needs Review</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <ImageUploadDropzone
            value={formData.featuredImageId}
            onChange={value => setFormData({ ...formData, featuredImageId: value })}
          />

          <div className="grid gap-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description || ''}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              placeholder="Enter description"
              rows={4}
            />
          </div>
        </CardContent>
      </Card>

      {/* Contact Section */}
      {!formData.usesHotelAddress && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Contact</CardTitle>
            <CardDescription>Contact Information for the Amenity</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-6 md:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="phoneNumber">Phone Number</Label>
              <Input
                id="phoneNumber"
                value={formData.phoneNumber || ''}
                onChange={e => setFormData({ ...formData, phoneNumber: e.target.value })}
                placeholder="Phone Number"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email || ''}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
                placeholder="Email Address"
              />
            </div>

            <div className="grid gap-2 md:col-span-2">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                value={formData.website || ''}
                onChange={e => setFormData({ ...formData, website: e.target.value })}
                placeholder="Website URL"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Location Section */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Location</CardTitle>
          <CardDescription>Location Information for the Amenity</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-6">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="usesHotelAddress"
              checked={formData.usesHotelAddress}
              onCheckedChange={checked =>
                setFormData({ ...formData, usesHotelAddress: checked as boolean })
              }
            />
            <label
              htmlFor="usesHotelAddress"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Uses Hotel Address
            </label>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="floor">Floor</Label>
              <Input
                id="floor"
                value={formData.floor || ''}
                onChange={e => setFormData({ ...formData, floor: e.target.value })}
                placeholder="Floor"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="capacity">Capacity</Label>
              <Input
                id="capacity"
                type="number"
                value={formData.capacity || ''}
                onChange={e =>
                  setFormData({ ...formData, capacity: parseInt(e.target.value) || undefined })
                }
                placeholder="Capacity"
              />
            </div>
          </div>

          {!formData.usesHotelAddress && (
            <div className="grid gap-6 md:grid-cols-2">
              <div className="grid gap-2 md:col-span-2">
                <Label htmlFor="streetAddress">Street Address</Label>
                <Input
                  id="streetAddress"
                  value={formData.streetAddress || ''}
                  onChange={e => setFormData({ ...formData, streetAddress: e.target.value })}
                  placeholder="Street Address"
                />
              </div>

              <div className="grid gap-2 md:col-span-2">
                <Label htmlFor="streetAddress2">Address Line 2</Label>
                <Input
                  id="streetAddress2"
                  value={formData.streetAddress2 || ''}
                  onChange={e => setFormData({ ...formData, streetAddress2: e.target.value })}
                  placeholder="Apartment, suite, etc."
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.city || ''}
                  onChange={e => setFormData({ ...formData, city: e.target.value })}
                  placeholder="City"
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  value={formData.state || ''}
                  onChange={e => setFormData({ ...formData, state: e.target.value })}
                  placeholder="State"
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="postalCode">Postal Code</Label>
                <Input
                  id="postalCode"
                  value={formData.postalCode || ''}
                  onChange={e => setFormData({ ...formData, postalCode: e.target.value })}
                  placeholder="Postal Code"
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={formData.country || ''}
                  onChange={e => setFormData({ ...formData, country: e.target.value })}
                  placeholder="Country"
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Hours Section */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Hours</CardTitle>
          <CardDescription>Business Hours</CardDescription>
        </CardHeader>
        <CardContent>
          <BusinessHoursEditor
            value={formData.hours}
            onChange={value =>
              setFormData({ ...formData, hours: value as Record<string, unknown> })
            }
          />
        </CardContent>
      </Card>

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(id ? `/amenities/${id}` : '/amenities')}
          >
            Cancel
          </Button>
          <Button type="submit" loading={isSaving} icon={Save}>
            {id ? 'Update Amenity' : 'Create Amenity'}
          </Button>
        </div>
      </form>
    </PageContainer>
  );
}
